import { useEffect, useMemo, useState } from 'react'
import { Modal } from 'antd'
import { Button, Spin } from '@crosscode/gravity-components'
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ClusterOutlined,
  DesktopOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { useUpdates } from './UpdatesProvider'
import {
  bodyParagraphs,
  filterUpdates,
  formatUpdateDate,
  uniqueTags,
  type UpdatesFilter,
} from './utils'
import styles from './NotificationsModal.module.css'

function tagIcon(tag?: string | null) {
  const t = (tag || '').toLowerCase()
  if (t.includes('интерф')) return <DesktopOutlined />
  if (t.includes('инфра')) return <ClusterOutlined />
  if (t.includes('систем')) return <ThunderboltOutlined />
  return <AppstoreOutlined />
}

export function NotificationsModal() {
  const {
    open,
    setOpen,
    items,
    loading,
    highlightIds,
    selectedId,
    setSelectedId,
    refresh,
  } = useUpdates()

  const [filter, setFilter] = useState<UpdatesFilter>('all')
  const [tag, setTag] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setFilter('all')
      setTag(null)
    }
  }, [open])

  const tags = useMemo(() => uniqueTags(items), [items])
  const visible = useMemo(
    () => filterUpdates(items, filter, highlightIds, tag),
    [items, filter, highlightIds, tag],
  )

  useEffect(() => {
    if (!open) return
    if (selectedId && visible.some((i) => i.id === selectedId)) return
    setSelectedId(visible[0]?.id ?? null)
  }, [open, visible, selectedId, setSelectedId])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      if (!visible.length) return
      e.preventDefault()
      const idx = Math.max(
        0,
        visible.findIndex((i) => i.id === selectedId),
      )
      const next =
        e.key === 'ArrowDown'
          ? visible[Math.min(idx + 1, visible.length - 1)]
          : visible[Math.max(idx - 1, 0)]
      if (next) setSelectedId(next.id)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, visible, selectedId, setSelectedId])

  const selected = visible.find((i) => i.id === selectedId) || visible[0] || null
  const newCount = highlightIds.size
  const paragraphs = selected ? bodyParagraphs(selected.body) : []

  const onClose = () => setOpen(false)

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      centered
      destroyOnHidden={false}
      closable={false}
      className={styles.modal}
      classNames={{ container: styles.modalContent, body: styles.modalBody }}
      styles={{ mask: { backdropFilter: 'blur(2px)' } }}
    >
      <div className={styles.shell}>
        <header className={styles.header}>
          <div className={styles.headerText}>
            <div className={styles.kicker}>KIT Support</div>
            <h2 className={styles.title}>Что нового</h2>
            <p className={styles.subtitle}>
              {items.length
                ? `${items.length} ${pluralUpdates(items.length)}${
                    newCount ? ` · ${newCount} ${pluralNew(newCount)}` : ''
                  }`
                : 'История изменений системы'}
            </p>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.iconBtn}
              aria-label="Обновить"
              title="Обновить"
              onClick={() => void refresh()}
            >
              <ReloadOutlined />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              aria-label="Закрыть"
              onClick={onClose}
            >
              <CloseOutlined />
            </button>
          </div>
        </header>

        <div className={styles.toolbar}>
          <div className={styles.seg}>
            <button
              type="button"
              className={`${styles.segBtn} ${filter === 'all' ? styles.segBtnActive : ''}`}
              onClick={() => setFilter('all')}
            >
              Все
            </button>
            <button
              type="button"
              className={`${styles.segBtn} ${filter === 'new' ? styles.segBtnActive : ''}`}
              onClick={() => setFilter('new')}
              disabled={newCount === 0}
            >
              Новые
              {newCount > 0 ? <span className={styles.segCount}>{newCount}</span> : null}
            </button>
          </div>
          {tags.length > 0 ? (
            <div className={styles.tags}>
              <button
                type="button"
                className={`${styles.tagChip} ${tag === null ? styles.tagChipActive : ''}`}
                onClick={() => setTag(null)}
              >
                Все темы
              </button>
              {tags.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`${styles.tagChip} ${tag === t ? styles.tagChipActive : ''}`}
                  onClick={() => setTag(tag === t ? null : t)}
                >
                  {t}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className={styles.content}>
          {loading && items.length === 0 ? (
            <div className={styles.state}>
              <Spin />
            </div>
          ) : visible.length === 0 ? (
            <div className={styles.state}>
              <CheckCircleOutlined className={styles.stateIcon} />
              <div className={styles.stateTitle}>
                {filter === 'new' ? 'Новых обновлений нет' : 'Пока нет обновлений'}
              </div>
              <div className={styles.stateHint}>
                {filter === 'new'
                  ? 'Вы уже посмотрели все свежие записи'
                  : 'Когда выйдет обновление — оно появится здесь'}
              </div>
            </div>
          ) : (
            <div className={styles.layout}>
              <aside className={styles.rail} aria-label="Список обновлений">
                {visible.map((item, index) => {
                  const isNew = highlightIds.has(item.id)
                  const active = selected?.id === item.id
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`${styles.railItem} ${active ? styles.railItemActive : ''} ${
                        isNew ? styles.railItemNew : ''
                      }`}
                      onClick={() => setSelectedId(item.id)}
                      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
                    >
                      <span className={styles.railDot} aria-hidden />
                      <span className={styles.railMain}>
                        <span className={styles.railTop}>
                          <span className={styles.railDate}>{formatUpdateDate(item.publishedAt)}</span>
                          {isNew ? <span className={styles.newBadge}>Новое</span> : null}
                        </span>
                        <span className={styles.railTitle}>{item.title}</span>
                        {item.tag ? <span className={styles.railTag}>{item.tag}</span> : null}
                      </span>
                    </button>
                  )
                })}
              </aside>

              <article className={styles.detail} key={selected?.id}>
                {selected ? (
                  <>
                    <div className={styles.detailHead}>
                      <div className={styles.detailIcon}>{tagIcon(selected.tag)}</div>
                      <div className={styles.detailMeta}>
                        <div className={styles.detailMetaRow}>
                          <span className={styles.detailDate}>
                            {formatUpdateDate(selected.publishedAt)}
                          </span>
                          {selected.tag ? (
                            <span className={styles.detailTag}>{selected.tag}</span>
                          ) : null}
                          {highlightIds.has(selected.id) ? (
                            <span className={styles.newBadge}>Новое</span>
                          ) : null}
                        </div>
                        <h3 className={styles.detailTitle}>{selected.title}</h3>
                      </div>
                    </div>
                    <div className={styles.detailBody}>
                      {paragraphs.map((p, i) => (
                        <p key={`${selected.id}-${i}`}>{p}</p>
                      ))}
                    </div>
                  </>
                ) : null}
              </article>
            </div>
          )}
        </div>

        <footer className={styles.footer}>
          <Button type="primary" onClick={onClose}>
            Понятно
          </Button>
        </footer>
      </div>
    </Modal>
  )
}

function pluralUpdates(n: number) {
  const m = n % 100
  const m10 = n % 10
  if (m > 10 && m < 20) return 'обновлений'
  if (m10 === 1) return 'обновление'
  if (m10 >= 2 && m10 <= 4) return 'обновления'
  return 'обновлений'
}

function pluralNew(n: number) {
  const m = n % 100
  const m10 = n % 10
  if (m > 10 && m < 20) return 'новых'
  if (m10 === 1) return 'новое'
  if (m10 >= 2 && m10 <= 4) return 'новых'
  return 'новых'
}
